/**
 * Kalash decoration — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_paint_crafts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const kalashDecorationPack: ToilePack = {
    slug: 'kalash-decoration',
    category: 'craft',
    name: 'Kalash decoration',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'painted', label: 'Painted', subject: 'a copper kalash painted with red and gold patterns'},
        {key: 'mirror', label: 'Mirror', subject: 'a kalash decorated with mirrors and lace'},
        {key: 'flower', label: 'Flower', subject: 'a kalash covered in marigolds and mango leaves'},
    ],
    styles: [
        {key: 'puja', label: 'Puja', style: 'set for a home puja with a coconut on top and a red and gold cloth tied at the neck', palette: 'vermilion, turmeric yellow and gold', framing: 'on a brass plate atop a wooden chowki strewn with rice grains and petals, front view'},
        {key: 'wedding', label: 'Wedding', style: 'dressed for a wedding with gota lace, pearls and a draped silk chunri', palette: 'deep red, gold and ivory', framing: 'on a silk-covered low table with a garland of roses, three-quarter view'},
        {key: 'navratri', label: 'Navratri', style: 'styled for Navratri ghatasthapana with sprouting barley at its base and a red chunri', palette: 'red, green and gold', framing: 'on a clay bed of sprouting grain beside a small brass diya, front view'},
    ],
    directions: ['Diwali', 'silver kalash', 'Ganesh puja'],
    directionHint: 'try: Diwali',
    seo: {
        title: 'Kalash decoration: painted, mirror, flower',
        metaDescription: 'Nine kalash decoration ideas for puja, wedding and Navratri: painted, mirror and marigold kalash in red, gold and green.',
        h1: 'Kalash decoration ideas: painted, mirror, marigold',
        tagline: 'Three decorated kalash, each set for a puja, a wedding or Navratri.',
        intro: 'A kalash is a pot dressed for a puja, a wedding or the Navratri festival. These nine show three decorated kalash: one painted in red and gold, one covered in mirrors and lace, and one covered in marigolds and mango leaves. Each is set for a home puja, for a wedding or for Navratri, with its own cloth, grain and flowers. Type a subject, such as Diwali or silver kalash, to ask for it.',
        useCases: [
            'a decorated kalash for a home puja',
            'a kalash for a wedding entrance',
            'a Navratri setup at home',
            'a table decoration for Diwali',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'Three decorated kalash: painted in red and gold, covered in mirrors and lace, and covered in marigolds and mango leaves. Each is set for a puja, a wedding or Navratri.'},
            {q: 'Are these photos of real kalash?', a: "No. They are generated pictures of each design, not photographs of a family's kalash."},
            {q: 'What goes on the kalash in each set?', a: 'The puja set has a coconut on top and a red and gold cloth tied at the neck. The Navratri set has sprouting barley at its base and a red chunri.'},
            {q: 'Can I ask for a different style?', a: 'Yes. Type a subject such as Diwali, silver kalash or Ganesh puja. A typed subject is a request, not a promised result.'},
        ],
    },
}
